import { captureException } from "@sentry/react"
import { actionAlert, Button, toast } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { MetaDataForPaidUser } from "api/resources/suraasaPlus/types"
import AsyncBuilder from "components/AsyncBuilder"
import { getPurchaseDetails } from "features/Payment/CartlessSubscriptionFlow"
import {
  getLogoSrc,
  SubscriptionSlugs,
} from "features/Payment/CartlessSubscriptionFlow/helper"
import { useSubscriptionPayment } from "features/Payment/CartlessSubscriptionFlow/useSubscriptionPayment"
import { PropsWithClassName } from "utils/types"

import { useSuraasaPlusContext } from "../context"

const UpdatePaymentDetailsButton = ({ className }: PropsWithClassName) => {
  const { subscriptionDetails } = useSuraasaPlusContext()
  const productSlug = subscriptionDetails?.product?.slug as SubscriptionSlugs

  const logoSrc = getLogoSrc(productSlug)

  const product = useQuery({
    queryFn: () => {
      return api.products.getProduct({
        params: {
          fields: ["prices"],
        },
        urlParams: { productSlug },
      })
    },
    queryKey: ["product", productSlug],
  })

  const getPriceObject = () => {
    if (!product.data) return null

    if (product.data.prices == null) {
      captureException(
        new Error("Product prices is null in update payment method flow"),
        scope => {
          scope.setExtras({
            productData: product.data,
          })
          return scope
        }
      )
      return
    }

    return {
      currency: product.data.prices.currency,
      itemPrice: product.data.prices.price,
      finalAmount: product.data.prices.price,
      discountAmount: 0,
      taxes: [],
    }
  }

  const payment = useSubscriptionPayment({
    purchaseDetails: getPurchaseDetails(product.data, getPriceObject()),
    logoSrc,
    stripeConfig: {
      redirectRoute: window.location.href,
    },
    razorpayConfig: {
      getUserDetails: () => ({}),
      onSuccess: async () => {
        await actionAlert.success({
          title: "Payment method updated successfully",
          duration: 5000,
        })
        window.location.reload()
      },
    },
  })
  return (
    <div>
      {payment.stripePaymentElement}
      <AsyncBuilder
        handler={async () => {
          if (!subscriptionDetails) {
            toast.error("Something went wrong!")
            return
          }
          await payment.updateSubscriptionPaymentMethod({
            paymentMethod: (
              subscriptionDetails.subscription.metaData as MetaDataForPaidUser
            ).paymentMethodCode,
            mandateId: subscriptionDetails.subscription.mandateId,
          })
        }}
        render={({ onClick, loading }) => {
          return (
            <Button
              color={null}
              loading={loading}
              className={className}
              onClick={onClick}
            >
              Update Payment Details
            </Button>
          )
        }}
      />
    </div>
  )
}

export default UpdatePaymentDetailsButton
